import {
  AppBar,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Link,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import { ThemeProvider, createTheme, styled } from "@mui/material/styles";
import createPalette from "@mui/material/styles/createPalette";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useParams } from "react-router-dom";
import { getPublicPhotograph } from "store/slices/ecommerce";
import FooterPage from "views/public/Footer";
import PhotographLogo from "views/ui-elements/PhotographLogo";

import { FormattedMessage } from "react-intl";
import FloatingCart from "views/public/FloatingCart";
import Error from "views/ui-elements/Error";

const Footer = styled("div")(({ theme }) => ({
  width: "100%",
  display: "block",
  position: "relative",
  backgroundColor: theme.palette.primary.main,
}));

const PhotographLayout = () => {
  const dispatch = useDispatch();
  const { photograph_id } = useParams();
  const { data, isLoading, error } = useSelector(
    (state) => state.ecommerce.photograph
  );

  useEffect(() => {
    dispatch(getPublicPhotograph(photograph_id));
  }, [dispatch, photograph_id]);

  if (isLoading) {
    return (
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={true}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  if (error) {
    return <Error />;
  }

  return (
    <ThemeProvider
      theme={(theme) =>
        createTheme({
          ...theme,
          palette: {
            ...theme.palette,
            primary: createPalette({
              main: data.primary_color,
            }),
            secondary: createPalette({
              main: data.secondary_color,
            }),
          },
        })
      }
    >
      <FloatingCart />
      <Container maxWidth="xl" sx={{ minHeight: "90vh" }} disableGutters>
        <AppBar
          position="sticky"
          color="inherit"
          elevation={0}
          sx={{ borderBottom: 1, borderColor: "divider", zIndex: 30000 }}
        >
          <Toolbar>
            <PhotographLogo
              photographId={data.aws_sub}
              photographName={data.name}
              photographUrlId={data.url_id}
            />
            <Box sx={{ flexGrow: 1 }} />
            <Stack
              direction="row"
              sx={{ display: { sm: "block" } }}
              spacing={2}
            >
              <Button
                color="inherit"
                component={Link}
                href={`/photographs/${data.url_id}`}
                mr={20}
              >
                <FormattedMessage id="menu.albums" />
              </Button>
              <Button
                color="inherit"
                component={Link}
                href={`/photographs/${data.url_id}/contact`}
                mr={20}
              >
                <FormattedMessage id="menu.contact" />
              </Button>
            </Stack>
          </Toolbar>
        </AppBar>
        <Outlet />
      </Container>
      <Footer sx={{ p: 6 }}>
        <Container>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Stack direction="column" gap={3}>
                <Typography variant="h4" color="white">
                  <FormattedMessage id="photograph.about_sportpxl" />
                </Typography>
                <Typography variant="body2" color="white">
                  <FormattedMessage id="photograph.description_sportpxl" />
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} md={3} textAlign="center">
              <Stack direction="column" gap={3}>
                <Typography variant="h4" color="white">
                  <FormattedMessage id="photograph.for_users" />
                </Typography>
                <Link href="/">
                  <Typography variant="body1" color="white">
                    <FormattedMessage id="public.home" />
                  </Typography>
                </Link>
                <Link href="/albums">
                  <Typography variant="body1" color="white">
                    <FormattedMessage id="album.all" />
                  </Typography>
                </Link>
                <Link
                  href="https://sportpxl.com"
                  target="_blank"
                  underline="none"
                >
                  <Typography variant="body1" color="white">
                    <FormattedMessage id="photograph.what_is_sportpxl" />
                  </Typography>
                </Link>
                <Link href="mailto:contact@sportpxl.com">
                  <Typography variant="body1" color="white">
                    <FormattedMessage id="general.actions.contact_us" />
                  </Typography>
                </Link>
              </Stack>
            </Grid>
            <Grid item xs={12} md={3} textAlign="center">
              <Stack direction="column" gap={3}>
                <Typography variant="h4" color="white">
                  <FormattedMessage id="photograph.for_photographs" />
                </Typography>
                <Link href="/dashboard">
                  <Typography variant="body1" color="white">
                    <FormattedMessage id="dashboard.access" />
                  </Typography>
                </Link>
                <Link href="/register">
                  <Typography variant="body1" color="white">
                    <FormattedMessage id="general.actions.register" />
                  </Typography>
                </Link>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Footer>
      <FooterPage />
    </ThemeProvider>
  );
};

export default PhotographLayout;
